import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import type { Column, RowData } from '@tanstack/react-table';
import { TableBody, TableCell, TableRow } from '../table/table';
import {
  dataTableMetaToCellProps,
  getDataTableColumnMeta,
} from './dataTableMetaProps';
import './dataTable.types';

export type DataTableSkeletonTableBodyProps<TData extends RowData> = {
  /** Number of skeleton rows (already clamped, e.g. from `rowsPerPage`). */
  rowCount: number;
  /** Visible leaf columns from the TanStack table (drives cells and alignment). */
  columns: Column<TData, unknown>[];
  /** Prefix for `data-testid` on each skeleton row. */
  testId?: string;
};

/**
 * Loading placeholder: a {@link TableBody} of skeleton rows aligned to column meta.
 * Kept separate from {@link DataTableView} so the main table render stays data-only.
 */
export function DataTableSkeletonTableBody<TData extends RowData>({
  rowCount,
  columns,
  testId,
}: DataTableSkeletonTableBodyProps<TData>): JSX.Element {
  const skeletonColumnCount = Math.max(columns.length, 1);

  return (
    <TableBody>
      {Array.from({ length: rowCount }, (_, rowIndex) => (
        <TableRow
          key={`data-table-skeleton-${rowIndex}`}
          hover={false}
          data-testid={
            testId !== undefined
              ? `${testId}-skeleton-row-${rowIndex}`
              : undefined
          }
        >
          {columns.length > 0
            ? columns.map((column, colIndex) => {
                const meta = getDataTableColumnMeta(column);
                const widthPct = 55 + ((rowIndex + colIndex) % 5) * 8;
                return (
                  <TableCell
                    key={`${rowIndex}-${column.id}`}
                    {...dataTableMetaToCellProps(meta)}
                  >
                    <Skeleton
                      variant='rectangular'
                      height={20}
                      width={`${widthPct}%`}
                      animation='wave'
                    />
                  </TableCell>
                );
              })
            : Array.from({ length: skeletonColumnCount }, (__, colIndex) => (
                <TableCell key={`${rowIndex}-placeholder-${colIndex}`}>
                  <Skeleton
                    variant='rectangular'
                    height={20}
                    width='70%'
                    animation='wave'
                  />
                </TableCell>
              ))}
        </TableRow>
      ))}
    </TableBody>
  );
}
